import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { apollo } from "../../apollo";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import Box from "@carrier-io/air-react/Box";
import { useAlerts } from "../../contexts/alert/AlertsContext";
import { AlertTypes } from "../../contexts/alert/types";
import { AdminHead } from "../../components/admin/AdminHead";
import { UserForm } from "../../components/userForm/UserForm";
import { EditUserStatus } from "../../components/editUser/EditUserStatus";
import { UserInputUpdate } from "../../validations/userEditValidation";
import { RoutesPage } from "../../constants/routes";
import { displayError } from "../../constants/error";
import { DEFAULT_REGION } from "../../constants/regions";
import { UserStatusT } from "../../constants/user";
import { useGetUser } from "../../hooks/useGetUser";
import { useUpdateUser } from "../../hooks/useUpdateUser";
import { UserSearchParam } from "../../types/user";

export const EditUser = () => {
  const navigate = useNavigate();
  const { pushAlert } = useAlerts();

  const [searchParams] = useSearchParams();
  const username = searchParams.get(UserSearchParam.USERNAME);
  const region = searchParams.get(UserSearchParam.REGION);

  const {
    data,
    loading: isLoading,
    refetch,
  } = useGetUser(username || "", region || "");
  const existingUser = data?.getUser;

  const [updateUser, { loading }] = useUpdateUser();

  const resolver = classValidatorResolver(UserInputUpdate);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<UserInputUpdate>({
    mode: "onBlur",
    resolver,
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      groups: [],
      roles: [],
      attributes: [],
    },
  });

  useEffect(() => {
    if (existingUser && !isLoading) {
      const attributesArray = existingUser.attributes
        ? Object.entries(existingUser.attributes).map(([key, value]) => ({
            key,
            value: String(value),
          }))
        : [];

      reset({
        email: existingUser.email,
        firstName: existingUser.firstName,
        lastName: existingUser.lastName,
        groups: existingUser.groups,
        roles: existingUser.roles,
        attributes: attributesArray,
      });
    }
  }, [existingUser, isLoading, reset]);

  const handleSaveUser = async (data: UserInputUpdate) => {
    const attributes = data.attributes?.reduce(
      (acc, item) => {
        if (item.key && item.value) {
          acc[item.key] = item.value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    const modifiedUser = {
      ...data,
      attributes,
    };

    try {
      await updateUser({
        variables: {
          username: existingUser?.username,
          input: modifiedUser,
          region: DEFAULT_REGION,
        },
      });
      pushAlert(AlertTypes.SUCCESS, "User updated successfully");
    } catch (error) {
      pushAlert(
        AlertTypes.ERROR,
        displayError((error as apollo.ApolloError).graphQLErrors[0]),
      );
    }
  };

  return (
    <>
      <AdminHead title="Edit User Profile" />
      <Box sx={{ padding: "24px 32px 40px", maxWidth: "922px" }}>
        <EditUserStatus
          status={existingUser?.status as UserStatusT}
          isLoading={isLoading}
          refetchExistingUser={refetch}
        />
        <UserForm
          control={control}
          errors={errors}
          onSubmit={handleSubmit(handleSaveUser)}
          loading={loading}
          loadingGetData={isLoading}
          onCancel={() => navigate(RoutesPage.UserManagement)}
          isValid={isDirty}
          submitButtonText="Save Changes"
          showAuthMethod={false}
          emailDisabled={true}
          showDeactivateUser={true}
          existingUserStatus={existingUser?.status as UserStatusT}
          refetchExistingUser={refetch}
        />
      </Box>
    </>
  );
};
